import React, { HTMLAttributes } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import calculateAspectRatio from "utils/calculateAspectRatio";
import imageSizes from "imageSizes";
import TextBoxWithImage, {
    TextBoxWithImageDescriptionBackgroundVariant,
} from "components/textBoxes/textboxWithImage/TextBoxWithImage";
import Scribble from "components/scribble/Scribble";
import { ScribbleVariant } from "components/scribble/ScribbleImage";
import Spacer from "components/Spacer";
import RouterLinkButton from "components/buttons/RouterLinkButton";
import { Typography } from "@material-ui/core";
import Rotate from "components/rotate/Rotate";

const useScribbleStyles = makeStyles((theme: Theme) => {
    return createStyles({
        v1ScribbleContainer: {
            top: "-4%",
            left: "-11%",
            width: "100%",
        },
        v2ScribbleContainer: {
            bottom: "-8%",
            left: "-11%",
            width: "100%",
        },
        v3ScribbleContainer: {
            top: "30%",
            left: "-14%",
            width: "100%",
        },
    });
});

export type DescriptionBackgroundVariant = TextBoxWithImageDescriptionBackgroundVariant;
export type FeatureTileScribbleVariant = "v1" | "v2" | "v3" | "none";

type IScribbleClasses = Partial<ReturnType<typeof useScribbleStyles>>;

interface IScribble {
    className?: string;
    variant: ScribbleVariant;
}

function getScribble(
    variant: FeatureTileScribbleVariant,
    classes: IScribbleClasses
): IScribble | undefined {
    if (variant === "v1") return { variant: "v1", className: classes.v1ScribbleContainer };
    if (variant === "v2") return { variant: "v1", className: classes.v2ScribbleContainer };
    if (variant === "v3") return { variant: "v3", className: classes.v3ScribbleContainer };
}

export interface IFeature {
    title: string;
    description: string;
    imgSrc: string;
    articleRoute?: string;
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
    feature: IFeature;
    descriptionBackgroundVariant?: TextBoxWithImageDescriptionBackgroundVariant;
    scribbleVariant?: FeatureTileScribbleVariant;
}

export default function FeatureTile(props: IProps) {
    const {
        feature,
        descriptionBackgroundVariant = "v1",
        scribbleVariant = "none",
        ...rest
    } = props;

    const { title, description, imgSrc, articleRoute } = feature;

    const scribbleClasses = useScribbleStyles();
    const scribble = getScribble(scribbleVariant, scribbleClasses);

    const content = (
        <TextBoxWithImage
            heading={title}
            imageProps={{
                src: imgSrc,
                aspectRatio: calculateAspectRatio(imageSizes.homePage.featuresTile),
            }}
            descriptionBackgroundVariant={descriptionBackgroundVariant}
            mobile
            {...rest}
        >
            {typeof description === "string" ? (
                <Typography style={{ lineHeight: 1.2 }}>{description}</Typography>
            ) : (
                description
            )}
            {typeof articleRoute === "undefined" ? null : (
                <>
                    <Spacer size={2} />
                    <Rotate amount={-2}>
                        <RouterLinkButton to={articleRoute}>Learn More</RouterLinkButton>
                    </Rotate>
                    <Spacer />
                </>
            )}
        </TextBoxWithImage>
    );

    return scribbleVariant === "none" ? (
        content
    ) : (
        <Scribble
            variant={scribble?.variant}
            classes={{
                scribbleContainer: scribble?.className,
            }}
        >
            {content}
        </Scribble>
    );
}
